import { useMemo } from "react";
import he from "he";
import { Blog } from "../../redux/types/blogTypes";
import { formatDate } from "../../utils";
import "./MarketplaceArticle.css";

const editions = {
  "/blog/about-fiddlepiddle-com": { kind: "about", label: "Our story" },
  "/blog/support": { kind: "support", label: "Help & information" },
  "/blog/our-protection-guarantee": { kind: "protection", label: "Marketplace policies" },
  "/blog/verified-sellers-fiddlepiddle-secure-transactions": { kind: "verified", label: "Seller verification" },
} as const;
export function hasMarketplaceEdition(url: string) {
  return Object.prototype.hasOwnProperty.call(editions, url);
}

/** Group existing adjacent content; text, links, IDs and media stay in Firestore. */
function sections(parent: Element, selector: string, className: string) {
  let section: HTMLElement | undefined;
  Array.from(parent.children).forEach((node) => {
    if (node.matches(selector)) {
      section = parent.ownerDocument.createElement("section");
      section.className = className;
      node.before(section);
    }
    section?.append(node);
  });
}

function arrange(blog: Blog, kind: string) {
  const doc = new DOMParser().parseFromString(he.decode(blog.blogContent), "text/html");
  const body = doc.body;
  let introduction = "";
  let media = "";
  let navigation = "";
  // Replace legacy presentational overrides only inside these selected editions.
  body.querySelectorAll("h1,h2,h3,h4,p,a,li,ol").forEach(el => el.removeAttribute("style"));
  body.querySelectorAll("h1").forEach(el => {
    const heading = doc.createElement("h2");
    heading.innerHTML = el.innerHTML;
    el.replaceWith(heading);
  });
  if (kind === "about") {
    const heroImage = body.querySelector("img");
    if (heroImage) { media = heroImage.outerHTML; heroImage.remove(); }
    const story = Array.from(body.children).find(el => el.querySelector("center h2"));
    if (story) {
      story.classList.add("marketplace-about-story");
      const title = story.querySelector("center h2");
      const copy = title?.parentElement?.nextElementSibling;
      const firstParagraph = copy?.querySelector("p");
      if (firstParagraph) { introduction = firstParagraph.outerHTML; firstParagraph.remove(); }
      if (title) { story.prepend(title); story.querySelector("center")?.remove(); }
      const image = copy?.querySelector("img");
      if (image) {
        const figure = doc.createElement("figure");
        figure.append(image);
        copy?.before(figure);
      }
      copy?.classList.add("marketplace-about-copy");
      body.prepend(story);
    }
    const definitions = body.querySelectorAll(".definition");
    if (definitions.length) {
      const dictionary = doc.createElement("section");
      dictionary.className = "marketplace-dictionary";
      const name = Array.from(body.children).find(el => el.tagName === "H2");
      if (name) dictionary.append(name);
      definitions.forEach(el => { el.removeAttribute("style"); dictionary.append(el); });
      const example = body.querySelector(".example");
      if (example) { example.removeAttribute("style"); dictionary.append(example); }
      body.append(dictionary);
    }
  }
  if (kind === "support") {
    const toc = body.querySelector("#toc");
    if (toc) { toc.removeAttribute("style"); navigation = toc.outerHTML; toc.remove(); }
    // The original contents list already links here; give its welcome text an anchor.
    if (!body.querySelector("#welcome")) body.querySelector("#additional + p")?.setAttribute("id", "welcome");
    sections(body, "h2,h3", "marketplace-support-topic");
    body.querySelectorAll(".marketplace-support-topic").forEach(section => {
      const id = section.firstElementChild?.id;
      if (id === "support" || id === "additional") section.classList.add("marketplace-support-topic--wide");
      if (id === "support") section.classList.add("marketplace-support-topic--contact");
    });
  }
  if (kind === "protection") {
    const policy = body.querySelector(".protection") || body;
    const firstParagraph = policy.querySelector("p");
    if (firstParagraph) { introduction = firstParagraph.outerHTML; firstParagraph.remove(); }
    policy.classList.add("marketplace-policy-grid");
    sections(policy, "h2,h3,h4", "marketplace-policy-section");
    policy.querySelectorAll(".marketplace-policy-section").forEach(section => {
      const heading = section.firstElementChild;
      if (heading?.tagName === "H2") section.classList.add("marketplace-policy-section--intro");
      if (heading?.tagName === "H3") section.classList.add("marketplace-policy-section--coverage");
      if (/shipping/i.test(heading?.textContent || "")) section.classList.add("marketplace-policy-section--shipping");
      if (section.querySelector("ol ul")) section.classList.add("marketplace-policy-section--process");
      if (/money back/i.test(heading?.textContent || "")) section.classList.add("marketplace-policy-section--refunds");
    });
  }
  if (kind === "verified") {
    const firstParagraph = body.querySelector("p");
    if (firstParagraph) { introduction = firstParagraph.outerHTML; firstParagraph.remove(); }
    const note = body.querySelector(".verification-note");
    note?.remove();
    sections(body, "h2", "marketplace-verified-section");
    const panels = body.querySelectorAll(".marketplace-verified-section");
    const listPanels = Array.from(panels).filter(panel => panel.querySelector("ul"));
    listPanels[0]?.classList.add("marketplace-verified-section--checks");
    listPanels[1]?.classList.add("marketplace-verified-section--benefits");
    listPanels.forEach(panel => panel.querySelectorAll("li > strong").forEach(label => {
      const following = label.nextSibling;
      if (following?.nodeType === 3 && following.textContent?.startsWith(":")) {
        label.append(doc.createTextNode(":"));
        following.textContent = following.textContent.slice(1);
      }
    }));
    panels.forEach(panel => {
      if (panel.querySelectorAll(":scope > p").length > 2 && !panel.querySelector("ul")) {
        panel.classList.add("marketplace-verified-section--reasons");
      }
    });
    panels[panels.length - 1]?.classList.add("marketplace-verified-section--closing");
    if (note) {
      if (listPanels[0]) listPanels[0].after(note);
      else body.append(note);
    }
  }
  return { introduction, media, navigation, body: body.innerHTML };
}

export function MarketplaceArticle({ blog }: { blog: Blog }) {
  const edition = editions[blog.blogUrl as keyof typeof editions];
  const content = useMemo(() => arrange(blog, edition.kind), [blog, edition.kind]);
  const milliseconds = String(blog.createdTime).length > 10 ? blog.createdTime : blog.createdTime * 1000;
  return (
    <article className={`marketplace-editorial marketplace-editorial--${edition.kind}`} aria-labelledby="marketplace-article-title">
      <div className="marketplace-editorial__hero">
        <div className="marketplace-editorial__welcome">
          <span className="marketplace-editorial__eyebrow">{edition.label}</span>
          <h1 id="marketplace-article-title">{blog.blogTitle}</h1>
          <p className="marketplace-editorial__date">Published <time dateTime={new Date(milliseconds).toISOString()}>{formatDate(blog.createdTime)}</time></p>
          {content.introduction ? <div className="marketplace-editorial__lead" dangerouslySetInnerHTML={{ __html: content.introduction }} /> : <p className="marketplace-editorial__lead">{blog.blogDescription}</p>}
        </div>
        {content.media && <div className="marketplace-editorial__hero-media" dangerouslySetInnerHTML={{ __html: content.media }} />}
        {edition.kind === "verified" && (
          <svg className="marketplace-editorial__verification-mark" viewBox="0 0 120 120" fill="none" aria-hidden="true">
            <circle cx="60" cy="60" r="56" fill="currentColor" fillOpacity=".06" />
            <path d="M60 19 91 31v26c0 20-14 35-31 44-17-9-31-24-31-44V31l31-12Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
            <path d="m43 58 12 12 23-25" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        <span className="marketplace-editorial__orbit" aria-hidden="true" />
      </div>
      <div className="marketplace-editorial__layout">
        {content.navigation && <nav className="marketplace-editorial__navigation" aria-label="Article contents" dangerouslySetInnerHTML={{ __html: content.navigation }} />}
        <div className="marketplace-editorial__body" dangerouslySetInnerHTML={{ __html: content.body }} />
      </div>
    </article>
  );
}
